import React from 'react';

// ================================================
// COGNITIVEOS DESIGN SYSTEM COMPONENTS
// Premium, bold, intelligent creative agency SaaS
// ================================================

// Design Tokens
export const DESIGN = {
  colors: {
    // Brand
    primary: '#00C9A7',
    primaryDark: '#00A88A',
    primaryLight: '#33D4B8',
    primaryGlow: 'rgba(0, 201, 167, 0.3)',
    primarySubtle: 'rgba(0, 201, 167, 0.08)',
    
    // Accents
    amber: '#F59E0B',
    amberGlow: 'rgba(245, 158, 11, 0.3)',
    amberSubtle: 'rgba(245, 158, 11, 0.08)',
    blue: '#3B82F6',
    blueGlow: 'rgba(59, 130, 246, 0.3)',
    blueSubtle: 'rgba(59, 130, 246, 0.08)',
    purple: '#8B5CF6',
    purpleGlow: 'rgba(139, 92, 246, 0.3)',
    
    // Status
    success: '#10B981',
    successSubtle: 'rgba(16, 185, 129, 0.08)',
    warning: '#F59E0B',
    warningSubtle: 'rgba(245, 158, 11, 0.08)',
    error: '#EF4444',
    errorSubtle: 'rgba(239, 68, 68, 0.08)',
    info: '#3B82F6',
    infoSubtle: 'rgba(59, 130, 246, 0.08)',
    
    // Surface
    background: '#F8FAFC',
    card: '#FFFFFF',
    border: '#E2E8F0',
    borderSubtle: '#F1F5F9',
    
    // Text
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    textMuted: '#94A3B8',
    textSubtle: '#CBD5E1',
    
    // LAB Dark Theme
    labBackground: '#0D1117',
    labCard: '#161B22',
    labBorder: '#30363D',
    labText: '#E6EDF3',
    labTextMuted: '#8B949E',
  },
  
  // 10 Definitive Cognitive Lane Colors
  lanes: {
    vision: { color: '#38BDF8', name: 'Vision & Expansion', glow: 'rgba(56, 189, 248, 0.3)' },       // Sky Blue
    strategy: { color: '#1E3A5F', name: 'Strategy & Architecture', glow: 'rgba(30, 58, 95, 0.3)' }, // Deep Navy
    creative: { color: '#A855F7', name: 'Creative', glow: 'rgba(168, 85, 247, 0.3)' },              // Vibrant Purple
    marketing: { color: '#FB923C', name: 'Marketing', glow: 'rgba(251, 146, 60, 0.3)' },            // Coral Orange
    operations: { color: '#22C55E', name: 'Operations', glow: 'rgba(34, 197, 94, 0.3)' },           // Forest Green
    execution: { color: '#14B8A6', name: 'Execution', glow: 'rgba(20, 184, 166, 0.3)' },            // Dark Teal
    revenue: { color: '#EAB308', name: 'Revenue', glow: 'rgba(234, 179, 8, 0.3)' },                 // Gold
    relationships: { color: '#F472B6', name: 'Relationships', glow: 'rgba(244, 114, 182, 0.3)' },   // Warm Rose
    culture: { color: '#C4B5FD', name: 'Culture', glow: 'rgba(196, 181, 253, 0.3)' },               // Soft Lavender
    finance: { color: '#64748B', name: 'Finance & Analysis', glow: 'rgba(100, 116, 139, 0.3)' },    // Steel Blue
  },
  
  // Cognitive Level Colors
  levels: {
    L4: { color: '#8B5CF6', name: 'Strategic', label: 'DECIDE + LEAD' },   // Purple
    L3: { color: '#3B82F6', name: 'Improvement', label: 'IMPROVE' },        // Blue
    L2: { color: '#10B981', name: 'Operational', label: 'RUN' },            // Green
    L1: { color: '#94A3B8', name: 'Execution', label: 'DO' },               // Gray
  },
  
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 2px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.10)',
    xl: '0 8px 32px rgba(0, 0, 0, 0.12)',
    glowPrimary: '0 0 20px rgba(0, 201, 167, 0.25)',
    glowAmber: '0 0 20px rgba(245, 158, 11, 0.25)',
  },
  
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
};

// ================================================
// PAGE HEADER COMPONENT
// ================================================
export const PageHeader = ({ title, subtitle, children, showGradient = true }) => (
  <div 
    className="mb-8 -mx-8 -mt-8 px-8 pt-8 pb-6"
    style={{ 
      background: showGradient 
        ? 'linear-gradient(180deg, rgba(0, 201, 167, 0.04) 0%, transparent 100%)'
        : 'transparent'
    }}
  >
    <div className="flex items-start justify-between">
      <div>
        <h1 
          className="text-3xl font-extrabold tracking-tight mb-1"
          style={{ color: DESIGN.colors.textPrimary }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
            {subtitle}
          </p>
        )}
      </div>
      {children && (
        <div className="flex items-center gap-3">
          {children}
        </div>
      )}
    </div>
  </div>
);

// ================================================
// PREMIUM CARD COMPONENT
// ================================================
export const PremiumCard = ({ 
  children, 
  className = '', 
  hoverable = true,
  accentColor = null,
  ...props 
}) => (
  <div
    className={`
      rounded-xl transition-all duration-200
      ${hoverable ? 'hover:shadow-lg hover:-translate-y-0.5' : ''}
      ${className}
    `}
    style={{
      backgroundColor: DESIGN.colors.card,
      border: `1px solid ${DESIGN.colors.border}`,
      boxShadow: DESIGN.shadows.md,
      borderLeft: accentColor ? `3px solid ${accentColor}` : undefined,
      ...props.style
    }}
    {...props}
  >
    {children}
  </div>
);

// ================================================
// METRIC CARD COMPONENT
// ================================================
export const MetricCard = ({ 
  label, 
  value, 
  trend, 
  trendValue, 
  status = 'default',
  icon: Icon,
  subtitle
}) => {
  const statusColors = {
    default: DESIGN.colors.primary,
    success: DESIGN.colors.success,
    warning: DESIGN.colors.amber,
    error: DESIGN.colors.error,
    info: DESIGN.colors.blue,
  };
  
  const accentColor = statusColors[status] || statusColors.default;
  
  return (
    <PremiumCard 
      accentColor={accentColor}
      className="p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <span 
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: DESIGN.colors.textMuted }}
        >
          {label}
        </span>
        {Icon && (
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            <Icon className="w-4 h-4" style={{ color: accentColor }} />
          </div>
        )}
      </div>
      
      <div className="flex items-end gap-2">
        <span 
          className="text-3xl font-bold tracking-tight"
          style={{ color: DESIGN.colors.textPrimary }}
        >
          {value}
        </span>
        {trend && (
          <span 
            className={`text-sm font-semibold flex items-center gap-1 ${
              trend === 'up' ? 'text-emerald-500' : 
              trend === 'down' ? 'text-red-500' : 
              'text-gray-500'
            }`}
          >
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
            {trendValue}
          </span>
        )}
      </div>
      
      {subtitle && (
        <p className="text-xs mt-2" style={{ color: DESIGN.colors.textMuted }}>
          {subtitle}
        </p>
      )}
    </PremiumCard>
  );
};

// ================================================
// STATUS BADGE COMPONENT
// ================================================
export const StatusBadge = ({ status, children, size = 'md' }) => {
  const configs = {
    'on-track': { bg: 'rgba(16, 185, 129, 0.12)', color: '#059669', border: 'rgba(16, 185, 129, 0.2)' },
    'completed': { bg: 'rgba(16, 185, 129, 0.08)', color: '#10B981', border: 'rgba(16, 185, 129, 0.15)' },
    'active': { bg: 'rgba(0, 201, 167, 0.12)', color: '#00A88A', border: 'rgba(0, 201, 167, 0.2)' },
    'in-progress': { bg: 'rgba(0, 201, 167, 0.12)', color: '#00A88A', border: 'rgba(0, 201, 167, 0.2)' },
    'focus-required': { bg: 'rgba(245, 158, 11, 0.12)', color: '#D97706', border: 'rgba(245, 158, 11, 0.2)' },
    'at-risk': { bg: 'rgba(245, 158, 11, 0.12)', color: '#D97706', border: 'rgba(245, 158, 11, 0.2)' },
    'warning': { bg: 'rgba(245, 158, 11, 0.12)', color: '#D97706', border: 'rgba(245, 158, 11, 0.2)' },
    'blocked': { bg: 'rgba(239, 68, 68, 0.12)', color: '#DC2626', border: 'rgba(239, 68, 68, 0.2)' },
    'critical': { bg: 'rgba(239, 68, 68, 0.12)', color: '#DC2626', border: 'rgba(239, 68, 68, 0.2)' },
    'overdue': { bg: 'rgba(239, 68, 68, 0.12)', color: '#DC2626', border: 'rgba(239, 68, 68, 0.2)' },
    'in-review': { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563EB', border: 'rgba(59, 130, 246, 0.2)' },
    'pending': { bg: 'rgba(148, 163, 184, 0.12)', color: '#64748B', border: 'rgba(148, 163, 184, 0.2)' },
    'default': { bg: 'rgba(148, 163, 184, 0.12)', color: '#64748B', border: 'rgba(148, 163, 184, 0.2)' },
  };
  
  const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '-') || 'default';
  const config = configs[normalizedStatus] || configs.default;
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-[10px]',
    md: 'px-2.5 py-1 text-[11px]',
    lg: 'px-3 py-1.5 text-xs',
  };
  
  return (
    <span
      className={`
        inline-flex items-center rounded-full font-semibold uppercase tracking-wide
        ${sizeClasses[size]}
      `}
      style={{
        backgroundColor: config.bg,
        color: config.color,
        boxShadow: `inset 0 0 0 1px ${config.border}`,
      }}
    >
      {children || status}
    </span>
  );
};

// ================================================
// PREMIUM PROGRESS BAR
// ================================================
export const PremiumProgress = ({ 
  value, 
  max = 100, 
  color = 'primary',
  showLabel = false,
  size = 'md',
  animated = true
}) => {
  const percentage = Math.min((value / max) * 100, 100);
  
  const colors = {
    primary: { from: '#00C9A7', to: '#33D4B8' },
    success: { from: '#10B981', to: '#34D399' },
    warning: { from: '#F59E0B', to: '#FBBF24' },
    error: { from: '#EF4444', to: '#F87171' },
    blue: { from: '#3B82F6', to: '#60A5FA' },
  };
  
  const colorConfig = colors[color] || colors.primary;
  
  const heights = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  };
  
  return (
    <div className="w-full">
      <div 
        className={`w-full rounded-full overflow-hidden ${heights[size]}`}
        style={{ backgroundColor: '#E2E8F0' }}
      >
        <div
          className="h-full rounded-full relative transition-all duration-500"
          style={{
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${colorConfig.from} 0%, ${colorConfig.to} 100%)`,
          }}
        >
          {animated && (
            <div
              className="absolute top-0 right-0 bottom-0 w-8 opacity-50"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4))',
                animation: 'shimmer 2s infinite',
              }}
            />
          )}
        </div>
      </div>
      {showLabel && (
        <div className="flex justify-between mt-1">
          <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{value}</span>
          <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{max}</span>
        </div>
      )}
    </div>
  );
};

// ================================================
// PREMIUM BUTTON
// ================================================
export const PremiumButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  className = '',
  ...props 
}) => {
  const variants = {
    primary: {
      background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
      color: 'white',
      hoverShadow: DESIGN.shadows.glowPrimary,
    },
    secondary: {
      background: 'transparent',
      color: DESIGN.colors.textSecondary,
      border: `1px solid ${DESIGN.colors.border}`,
    },
    ghost: {
      background: 'transparent',
      color: DESIGN.colors.textSecondary,
    },
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  
  const config = variants[variant];
  
  return (
    <button
      className={`
        inline-flex items-center justify-center gap-2 rounded-lg font-semibold
        transition-all duration-200 hover:-translate-y-0.5
        ${sizes[size]}
        ${className}
      `}
      style={{
        background: config.background,
        color: config.color,
        border: config.border || 'none',
        boxShadow: DESIGN.shadows.sm,
      }}
      onMouseEnter={(e) => {
        if (config.hoverShadow) {
          e.currentTarget.style.boxShadow = config.hoverShadow;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = DESIGN.shadows.sm;
      }}
      {...props}
    >
      {Icon && iconPosition === 'left' && <Icon className="w-4 h-4" />}
      {children}
      {Icon && iconPosition === 'right' && <Icon className="w-4 h-4" />}
    </button>
  );
};

// ================================================
// SECTION HEADER
// ================================================
export const SectionHeader = ({ title, subtitle, action }) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <h2 
        className="text-lg font-bold"
        style={{ color: DESIGN.colors.textPrimary }}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
          {subtitle}
        </p>
      )}
    </div>
    {action}
  </div>
);

// ================================================
// CHART CONTAINER
// ================================================
export const ChartContainer = ({ title, subtitle, children, className = '' }) => (
  <PremiumCard className={`p-6 ${className}`} hoverable={false}>
    {(title || subtitle) && (
      <div className="mb-4">
        {title && (
          <h3 className="text-base font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="text-sm mt-0.5" style={{ color: DESIGN.colors.textMuted }}>
            {subtitle}
          </p>
        )}
      </div>
    )}
    {children}
  </PremiumCard>
);

export default {
  DESIGN,
  PageHeader,
  PremiumCard,
  MetricCard,
  StatusBadge,
  PremiumProgress,
  PremiumButton,
  SectionHeader,
  ChartContainer,
};
